import { Component, Input, ViewEncapsulation, AfterViewInit, Output, EventEmitter, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { DateTime as LuxonDateTime } from 'luxon';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { AssignedProModel, HideAssignedProDidNotContactPipe } from 'src/shared/pipes/hide-assigned-pro-did-not-contact.pipe';

@Component({
    selector: 'tidy-notification-history',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./notification-history.component.scss'],
    templateUrl: './notification-history.component.html',
    standalone: false
})

export class NotificationHistoryComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() items: any;
  @Output() selectionChange = new EventEmitter<any>();
  @Output() viewProAction = new EventEmitter<string>();
  @Output() viewHistoryDetails = new EventEmitter<any>();
  @Input() notificationHistory: any;
  @Input() isLoadingNotificationHistory = false;
  @Input() isSearchingForPro = true;
  @Input() hasQuotes = false;
  @Input() assignedPros: AssignedProModel[] = [];
  showFullHistory = false;
  lastCanceledIndex = -1;

  readonly statusTexts: string[] = [
    'Texting pros',
    'Reaching out to local pros',
    'Searching your supported channels',
    'Comparing pro ratings',
    'Checking our network',
    'Checking schedules',
    'Calling a pro',
    'Reviewing pro qualifications',
    'Reviewing potential quotes',
    'Sending your request details',
    'Verifying availability',
    'Confirming service areas',
    'Analyzing project details',
    'Negotiating best rates',
    'Matching with specialists',
    'Screening pro profiles',
    'Coordinating with top-rated pros',
    'Following up with interested pros'
  ];
  currentStatusIndex = 0;
  currentStatusText = this.statusTexts[0];
  statusTextVisible = true;
  private statusInterval: ReturnType<typeof setInterval> | null = null;

  private computedValuesCalculated = false;

  constructor(
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.startStatusCycle();
  }

  ngAfterViewInit() {
    this.calculateComputedValues();
  }

  ngOnDestroy() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
    }
  }

  private startStatusCycle() {
    this.statusInterval = setInterval(() => {
      this.statusTextVisible = false;
      setTimeout(() => {
        this.currentStatusIndex = (this.currentStatusIndex + 1) % this.statusTexts.length;
        this.currentStatusText = this.statusTexts[this.currentStatusIndex];
        this.statusTextVisible = true;
      }, 300);
    }, 3000);
  }

  toggleItem(item: any) {
    if (!item.data.happened) {
      return;
    }
    if (item.computedIsNewPros) {
      return;
    }
    item['expanded'] = !item['expanded'];
    if (item['expanded']) {
      // Rows can be filtered out of the view, so emit the index in the unfiltered items the parent holds.
      this.selectionChange.emit({ selectedIndex: this.items.indexOf(item) });
    }
  }

  private calculateComputedValues() {
    if (this.computedValuesCalculated || !this.items) {
      return;
    }
    this.items.forEach(item => {
      if (item?.data?.not_contacted_reason) {
        item.computedNotContactedReason = item.data.not_contacted_reason;
      }
      if (item?.data?.declined_reason) {
        item.computedDeclinedString = this.builDeclinedString(item.data.declined_reason);
      }
      if (item?.data?.declined_show_at) {
        item.computedDeclinedDate = this.buildAbsoluteDate(item.data.declined_show_at);
      }
      if (item?.data?.show_at && item?.data?.hours_until_next_contact > 0) {
        item.computedShowDate = this.buildDate(item.data.show_at);
        item.computedShowDateAbsolute = this.buildAbsoluteDate(item.data.show_at);
      }
      const actionDateSource =
        item?.data?.type === 'accepted' ? item.data.accepted_at :
        item?.data?.type === 'declined' ? item.data.declined_at :
        item?.data?.type === 'canceled' ? item.data.canceled_at :
        item?.data?.type === 'not_contacted' ? item.data.resolved_at :
        item?.data?.type === 'contacted' ? item.data.contacted_at :
        null;
      if (actionDateSource) {
        item.computedActionDate = this.buildDate(actionDateSource);
      }
      if (item?.data?.contacted_at) {
        item.computedContactedDateAbsolute = this.buildAbsoluteDate(item.data.contacted_at);
      }
      if (item?.data?.declined_at) {
        item.computedDeclinedAtAbsolute = this.buildAbsoluteDate(item.data.declined_at);
      }
      if (item?.data?.accepted_at) {
        item.computedAcceptedDateAbsolute = this.buildAbsoluteDate(item.data.accepted_at);
      }
      if (item?.data?.canceled_at) {
        item.computedCanceledDateAbsolute = this.buildAbsoluteDate(item.data.canceled_at);
      }
      if (item?.data?.resolved_at) {
        item.computedResolvedDateAbsolute = this.buildAbsoluteDate(item.data.resolved_at);
      }
      item.computedIsNewPros = item.text?.includes('New Pros') || false;
      item.computedIconType = (this.isSearchingForPro && item.computedIsNewPros && item.data.happened && !item.data.resolved_at) ? 'thinking' : 'waiting';
    });

    if (!this.isSearchingForPro) {
      this.items.forEach(item => item.computedShouldPause = true);
    } else {
      const acceptedIndex = this.items.findIndex(item => item?.data?.type === 'accepted');
      if (acceptedIndex !== -1) {
        const canceledIndex = this.items.findIndex((item, index) => index > acceptedIndex && item?.data?.type === 'canceled');
        if (canceledIndex === -1) {
          this.items.forEach(item => item.computedShouldPause = true);
        } else {
          this.items.forEach((item, index) => {
            item.computedShouldPause = index <= canceledIndex;
          });
        }
      }
    }

    // The template compares this against indexes of the filtered list, so compute it on that list.
    const visibleItems = new HideAssignedProDidNotContactPipe().transform(this.items, this.assignedPros);
    for (let i = visibleItems.length - 1; i >= 0; i--) {
      if (visibleItems[i]?.data?.type === 'canceled') {
        this.lastCanceledIndex = i;
        break;
      }
    }

    this.computedValuesCalculated = true;
  }

  toggleFullHistory() {
    this.showFullHistory = !this.showFullHistory;
  }

  buildDate(date) {
    const timezone = LuxonDateTime.local().zoneName;
    const dateInTimezone = LuxonDateTime.fromISO(date).setZone(timezone);
    const locale = localStorage.getItem('language') || 'en';
    return dateInTimezone.toRelative({ locale }) || '';
  }

  buildAbsoluteDate(date) {
    const timezone = LuxonDateTime.local().zoneName;
    const dateInTimezone = LuxonDateTime.fromISO(date).setZone(timezone).toISO();
    const day = new ParseDateTimePipe().transform(dateInTimezone, 'ddd MMM d');
    const time = new ParseDateTimePipe().transform(dateInTimezone, 'h:mma');
    return `${day} ${time}`;
  }

  builDeclinedString(text) {
    let newText = '';
    newText = text.replace("The pro declined,", "");
    newText = newText.replace("saying", "said");
    return newText;
  }

  viewPro(proId) {
    this.viewProAction.emit(proId);
  }

  viewHistory(history, type) {
    history.notification_type = type;
    this.viewHistoryDetails.emit(history);
  }
}
